import { useState, useEffect, useCallback } from 'react';
import { phoneAnalyticsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import {
  Phone, PhoneMissed, MessageSquare,
  Clock, Activity, TrendingUp, Loader2, Hash, Target,
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer,
} from 'recharts';

const RANGE_OPTIONS = [
  { key: 'today', label: 'Today' },
  { key: '7d', label: '7 Days' },
  { key: '30d', label: '30 Days' },
  { key: 'cycle', label: 'Billing Cycle' },
];

// ─── Metric Card ───────────────────────────────────────────
function MetricCard({ label, value, sub, icon: Icon, accent, testId }) {
  const accentClasses = {
    red: 'text-red-600',
    green: 'text-green-600',
    amber: 'text-amber-600',
    blue: 'text-blue-600',
  };
  const iconBgClasses = {
    red: 'bg-red-100 text-red-600',
    green: 'bg-green-100 text-green-600',
    amber: 'bg-amber-100 text-amber-600',
    blue: 'bg-blue-100 text-blue-600',
  };
  const valueColor = accentClasses[accent] || 'text-foreground';
  const iconBg = iconBgClasses[accent] || 'bg-muted text-muted-foreground';

  return (
    <div className="bg-muted/40 border border-border/50 rounded-lg p-3 sm:p-4" data-testid={testId}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-[10px] sm:text-xs text-muted-foreground font-medium uppercase tracking-wider truncate">{label}</p>
          <p className={`text-lg sm:text-xl font-bold mt-0.5 tabular-nums ${valueColor}`}>{value}</p>
          {sub && <p className="text-[10px] sm:text-xs text-muted-foreground mt-0.5 truncate">{sub}</p>}
        </div>
        {Icon && (
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${iconBg}`}>
            <Icon className="w-4 h-4" />
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Minutes Gauge ─────────────────────────────────────────
function MinutesGauge({ used, included, overage, tierName, overageChargeCents }) {
  const pct = included > 0 ? Math.min(100, (used / included) * 100) : 0;
  const isOver = overage > 0;
  const barColor = isOver ? 'bg-red-500' : pct > 80 ? 'bg-amber-500' : 'bg-emerald-500';

  return (
    <Card data-testid="minutes-gauge-card">
      <CardContent className="p-3 sm:p-5">
        <div className="flex items-center justify-between mb-2.5 sm:mb-3 gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-blue-100 flex items-center justify-center shrink-0">
              <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-600" />
            </div>
            <div className="min-w-0">
              <p className="text-xs sm:text-sm font-semibold truncate">Minutes This Cycle</p>
              <p className="text-[10px] sm:text-xs text-muted-foreground">{tierName} plan</p>
            </div>
          </div>
          {isOver && (
            <Badge variant="destructive" className="text-[10px] shrink-0" data-testid="overage-badge">
              {overage} min over
            </Badge>
          )}
        </div>

        {/* Progress bar */}
        <div className="w-full bg-muted rounded-full h-2.5 sm:h-3 overflow-hidden" data-testid="minutes-progress-bar">
          <div
            className={`h-full rounded-full transition-all duration-500 ${barColor}`}
            style={{ width: `${Math.min(100, pct)}%` }}
          />
        </div>

        <div className="flex items-center justify-between mt-1.5 sm:mt-2">
          <p className="text-[11px] sm:text-xs text-muted-foreground tabular-nums">
            <span className="font-semibold text-foreground">{used.toLocaleString()}</span> / {included.toLocaleString()} min
          </p>
          <p className="text-[11px] sm:text-xs tabular-nums text-muted-foreground">
            {pct.toFixed(0)}%
          </p>
        </div>

        {isOver && overageChargeCents > 0 && (
          <div className="mt-2 bg-red-50 border border-red-200 rounded-md px-3 py-1.5" data-testid="overage-charge-info">
            <p className="text-xs text-red-700">
              Estimated overage charge: <span className="font-semibold">${(overageChargeCents / 100).toFixed(2)}</span>
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Trend Chart ───────────────────────────────────────────
function TrendChart({ series, granularity }) {
  if (!series || series.length === 0) {
    return (
      <Card data-testid="trend-chart-card">
        <CardContent className="p-4 flex items-center justify-center h-48 text-sm text-muted-foreground">
          No trend data available for this period.
        </CardContent>
      </Card>
    );
  }

  const formatLabel = (dateStr) => {
    try {
      const d = new Date(dateStr);
      if (granularity === 'hour') {
        return d.toLocaleTimeString([], { hour: 'numeric' });
      }
      return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
    } catch { return dateStr; }
  };

  const formatted = series.map(s => ({ ...s, label: formatLabel(s.date) }));

  // On mobile, limit visible ticks to avoid cramming
  const tickInterval = formatted.length > 14 ? Math.ceil(formatted.length / 7) - 1 : formatted.length > 7 ? 1 : 0;

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-background border rounded-lg shadow-md px-3 py-2 text-xs">
        <p className="font-medium mb-1">{label}</p>
        {payload.map((p, i) => (
          <div key={i} className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full" style={{ background: p.color }} />
            <span className="text-muted-foreground">{p.name}:</span>
            <span className="font-medium tabular-nums">{p.value}</span>
          </div>
        ))}
      </div>
    );
  };

  const renderLegend = () => (
    <div className="flex items-center justify-center gap-4 sm:gap-5 pt-3 pb-1">
      <div className="flex items-center gap-1.5">
        <div className="w-3 h-[3px] rounded-full bg-[#3b82f6]" />
        <span className="text-[11px] text-muted-foreground">Calls</span>
      </div>
      <div className="flex items-center gap-1.5">
        <div className="w-3 h-[3px] rounded-full bg-[#ef4444]" style={{ borderBottom: '1px dashed #ef4444', height: 0 }} />
        <span className="text-[11px] text-muted-foreground">Missed</span>
      </div>
      <div className="flex items-center gap-1.5">
        <div className="w-3 h-[3px] rounded-full bg-[#8b5cf6]" />
        <span className="text-[11px] text-muted-foreground">SMS</span>
      </div>
    </div>
  );

  return (
    <Card data-testid="trend-chart-card">
      <CardHeader className="pb-1 px-3 sm:px-4 pt-3 sm:pt-4">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-muted-foreground" />
          <CardTitle className="text-sm font-semibold">Activity Trends</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="px-1 sm:px-4 pb-2 sm:pb-4">
        <div className="h-52 sm:h-64" data-testid="trend-chart-container">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={formatted} margin={{ top: 8, right: 12, left: -16, bottom: 0 }}>
              <defs>
                <linearGradient id="callGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="smsGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 10 }}
                tickLine={false}
                axisLine={false}
                interval={tickInterval}
                angle={formatted.length > 14 ? -35 : 0}
                textAnchor={formatted.length > 14 ? 'end' : 'middle'}
                height={formatted.length > 14 ? 45 : 30}
              />
              <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} allowDecimals={false} width={30} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="calls" name="Calls" stroke="#3b82f6" fill="url(#callGrad)" strokeWidth={2} dot={false} />
              <Area type="monotone" dataKey="sms" name="SMS" stroke="#8b5cf6" fill="url(#smsGrad)" strokeWidth={2} dot={false} />
              <Area type="monotone" dataKey="missed" name="Missed" stroke="#ef4444" fill="none" strokeWidth={1.5} strokeDasharray="4 3" dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        {renderLegend()}
      </CardContent>
    </Card>
  );
}

// ─── Per-Number Table ──────────────────────────────────────
function PerNumberTable({ numbers }) {
  if (!numbers || numbers.length === 0) {
    return (
      <Card data-testid="per-number-card">
        <CardHeader className="pb-0 px-3 sm:px-4 pt-3 sm:pt-4">
          <div className="flex items-center gap-2">
            <Hash className="w-4 h-4 text-muted-foreground" />
            <CardTitle className="text-sm font-semibold">Per-Number Activity</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="p-3 sm:p-4 text-sm text-muted-foreground text-center py-6 sm:py-8">
          No active numbers to display.
        </CardContent>
      </Card>
    );
  }

  const formatPhone = (e164) => {
    if (!e164) return '';
    const digits = e164.replace(/\D/g, '');
    if (digits.length === 11 && digits.startsWith('1')) {
      return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    }
    return e164;
  };

  return (
    <Card data-testid="per-number-card">
      <CardHeader className="pb-2 px-3 sm:px-4 pt-3 sm:pt-4">
        <div className="flex items-center gap-2">
          <Hash className="w-4 h-4 text-muted-foreground" />
          <CardTitle className="text-sm font-semibold">Per-Number Activity</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="px-0 pb-2">
        {/* Desktop table */}
        <div className="hidden sm:block overflow-x-auto">
          <table className="w-full text-sm" data-testid="per-number-table">
            <thead>
              <tr className="border-b text-xs text-muted-foreground uppercase tracking-wider">
                <th className="text-left px-4 py-2.5 font-medium">Number</th>
                <th className="text-left px-2 py-2.5 font-medium">Source</th>
                <th className="text-center px-2 py-2.5 font-medium">Inbound</th>
                <th className="text-center px-2 py-2.5 font-medium">Outbound</th>
                <th className="text-center px-2 py-2.5 font-medium">Missed</th>
                <th className="text-center px-2 py-2.5 font-medium">SMS</th>
                <th className="text-center px-2 py-2.5 font-medium">Minutes</th>
                <th className="text-left px-4 py-2.5 font-medium">Call Flow</th>
              </tr>
            </thead>
            <tbody>
              {numbers.map(n => (
                <tr key={n.id} className="border-b last:border-0 hover:bg-muted/30 transition-colors" data-testid={`number-row-${n.id}`}>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <span className="font-medium tabular-nums">{formatPhone(n.phone_number)}</span>
                      {n.is_primary && <Badge variant="outline" className="text-[9px] px-1.5 py-0">Primary</Badge>}
                    </div>
                    {n.friendly_name && <p className="text-xs text-muted-foreground">{n.friendly_name}</p>}
                  </td>
                  <td className="px-2 py-2.5">
                    {n.lead_source_name ? (
                      <Badge className="bg-violet-100 text-violet-700 text-[9px]">
                        <Target className="w-2.5 h-2.5 mr-0.5" />{n.lead_source_name}
                      </Badge>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="text-center px-2 py-2.5 tabular-nums">{n.calls_inbound}</td>
                  <td className="text-center px-2 py-2.5 tabular-nums">{n.calls_outbound}</td>
                  <td className="text-center px-2 py-2.5 tabular-nums">
                    <span className={n.calls_missed > 0 ? 'text-red-600 font-medium' : ''}>{n.calls_missed}</span>
                  </td>
                  <td className="text-center px-2 py-2.5 tabular-nums">{n.sms_count}</td>
                  <td className="text-center px-2 py-2.5 tabular-nums">{n.total_minutes}</td>
                  <td className="px-4 py-2.5 text-xs text-muted-foreground">{n.call_flow || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className="sm:hidden space-y-2 px-3">
          {numbers.map(n => (
            <div key={n.id} className="border rounded-lg p-3 space-y-2" data-testid={`number-card-mobile-${n.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5 min-w-0">
                  <span className="text-sm font-medium tabular-nums">{formatPhone(n.phone_number)}</span>
                  {n.is_primary && <Badge variant="outline" className="text-[9px] px-1 py-0">Primary</Badge>}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {n.lead_source_name && (
                    <Badge className="bg-violet-100 text-violet-700 text-[9px]">
                      <Target className="w-2 h-2 mr-0.5" />{n.lead_source_name}
                    </Badge>
                  )}
                  {n.call_flow && <span className="text-[10px] text-muted-foreground truncate max-w-[80px]">{n.call_flow}</span>}
                </div>
              </div>
              <div className="grid grid-cols-5 gap-1 text-center">
                <div>
                  <p className="text-[10px] text-muted-foreground">In</p>
                  <p className="text-xs font-semibold tabular-nums">{n.calls_inbound}</p>
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground">Out</p>
                  <p className="text-xs font-semibold tabular-nums">{n.calls_outbound}</p>
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground">Miss</p>
                  <p className={`text-xs font-semibold tabular-nums ${n.calls_missed > 0 ? 'text-red-600' : ''}`}>{n.calls_missed}</p>
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground">SMS</p>
                  <p className="text-xs font-semibold tabular-nums">{n.sms_count}</p>
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground">Min</p>
                  <p className="text-xs font-semibold tabular-nums">{n.total_minutes}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// ─── By-Source Rollup ──────────────────────────────────────
function BySourceRollup({ sources }) {
  if (!sources || sources.length === 0) return null;

  return (
    <Card data-testid="by-source-card">
      <CardHeader className="pb-2 px-3 sm:px-4 pt-3 sm:pt-4">
        <div className="flex items-center gap-2">
          <Target className="w-4 h-4 text-muted-foreground" />
          <CardTitle className="text-sm font-semibold">Activity by Source</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="px-0 pb-2">
        {/* Desktop */}
        <div className="hidden sm:block overflow-x-auto">
          <table className="w-full text-sm" data-testid="by-source-table">
            <thead>
              <tr className="border-b text-xs text-muted-foreground uppercase tracking-wider">
                <th className="text-left px-4 py-2.5 font-medium">Source</th>
                <th className="text-center px-2 py-2.5 font-medium">Numbers</th>
                <th className="text-center px-2 py-2.5 font-medium">Calls</th>
                <th className="text-center px-2 py-2.5 font-medium">Missed</th>
                <th className="text-center px-2 py-2.5 font-medium">SMS</th>
                <th className="text-center px-2 py-2.5 font-medium">Minutes</th>
              </tr>
            </thead>
            <tbody>
              {sources.map(s => (
                <tr key={s.lead_source_id} className="border-b last:border-0 hover:bg-muted/30 transition-colors" data-testid={`source-row-${s.lead_source_id}`}>
                  <td className="px-4 py-2.5">
                    <Badge className="bg-violet-100 text-violet-700 text-[10px]">
                      <Target className="w-2.5 h-2.5 mr-0.5" />{s.lead_source_name}
                    </Badge>
                  </td>
                  <td className="text-center px-2 py-2.5 tabular-nums">{s.number_count}</td>
                  <td className="text-center px-2 py-2.5 tabular-nums">{s.calls_total}</td>
                  <td className="text-center px-2 py-2.5 tabular-nums">
                    <span className={s.calls_missed > 0 ? 'text-red-600 font-medium' : ''}>{s.calls_missed}</span>
                  </td>
                  <td className="text-center px-2 py-2.5 tabular-nums">{s.sms_total}</td>
                  <td className="text-center px-2 py-2.5 tabular-nums">{s.total_minutes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Mobile */}
        <div className="sm:hidden space-y-2 px-3">
          {sources.map(s => (
            <div key={s.lead_source_id} className="border rounded-lg p-3 space-y-2" data-testid={`source-card-mobile-${s.lead_source_id}`}>
              <div className="flex items-center justify-between">
                <Badge className="bg-violet-100 text-violet-700 text-[10px]">
                  <Target className="w-2.5 h-2.5 mr-0.5" />{s.lead_source_name}
                </Badge>
                <span className="text-[10px] text-muted-foreground">{s.number_count} number{s.number_count !== 1 ? 's' : ''}</span>
              </div>
              <div className="grid grid-cols-4 gap-1 text-center">
                <div>
                  <p className="text-[10px] text-muted-foreground">Calls</p>
                  <p className="text-xs font-semibold tabular-nums">{s.calls_total}</p>
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground">Missed</p>
                  <p className={`text-xs font-semibold tabular-nums ${s.calls_missed > 0 ? 'text-red-600' : ''}`}>{s.calls_missed}</p>
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground">SMS</p>
                  <p className="text-xs font-semibold tabular-nums">{s.sms_total}</p>
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground">Min</p>
                  <p className="text-xs font-semibold tabular-nums">{s.total_minutes}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// ═══════════════════════════════════════════════════════════
// MAIN USAGE DASHBOARD TAB
// ═══════════════════════════════════════════════════════════
export default function UsageDashboardTab() {
  const [range, setRange] = useState('cycle');
  const [dashboard, setDashboard] = useState(null);
  const [trends, setTrends] = useState(null);
  const [perNumber, setPerNumber] = useState(null);
  const [bySource, setBySource] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (r) => {
    setLoading(true);
    try {
      const [dashRes, trendRes, numRes, srcRes] = await Promise.all([
        phoneAnalyticsAPI.dashboard(r),
        phoneAnalyticsAPI.trends(r),
        phoneAnalyticsAPI.perNumber(r),
        phoneAnalyticsAPI.bySource(r),
      ]);
      setDashboard(dashRes.data);
      setTrends(trendRes.data);
      setPerNumber(numRes.data);
      setBySource(srcRes.data);
    } catch (err) {
      toast.error('Failed to load usage data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(range); }, [range, load]);

  const handleRangeChange = (r) => {
    setRange(r);
  };

  if (loading && !dashboard) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="usage-loading">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const d = dashboard;
  const callMinutes = d?.calls?.total_minutes ?? 0;

  return (
    <div className="space-y-4 sm:space-y-5" data-testid="usage-dashboard-tab">
      {/* Range selector */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm font-medium text-muted-foreground">Period</span>
        </div>
        <div className="flex gap-1 bg-muted/50 rounded-lg p-0.5 w-full sm:w-auto" data-testid="range-selector">
          {RANGE_OPTIONS.map(opt => (
            <button
              key={opt.key}
              onClick={() => handleRangeChange(opt.key)}
              className={`flex-1 sm:flex-initial px-2.5 sm:px-3.5 py-1.5 sm:py-1.5 rounded-md text-xs sm:text-sm transition-colors whitespace-nowrap ${
                range === opt.key
                  ? 'bg-background shadow-sm font-medium text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid={`range-${opt.key}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Summary metric cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-2.5 sm:gap-3" data-testid="usage-metrics-grid">
        <MetricCard
          label="Total Calls"
          value={d?.calls?.total ?? 0}
          sub={`${d?.calls?.inbound ?? 0} in / ${d?.calls?.outbound ?? 0} out`}
          icon={Phone}
          accent="blue"
          testId="metric-total-calls"
        />
        <MetricCard
          label="Missed Calls"
          value={d?.calls?.missed ?? 0}
          sub={d?.calls?.total > 0 ? `${((d.calls.missed / d.calls.total) * 100).toFixed(0)}% miss rate` : null}
          icon={PhoneMissed}
          accent={d?.calls?.missed > 0 ? 'red' : undefined}
          testId="metric-missed-calls"
        />
        <MetricCard
          label="SMS Messages"
          value={d?.sms?.total ?? 0}
          sub={`${d?.sms?.inbound ?? 0} in / ${d?.sms?.outbound ?? 0} out`}
          icon={MessageSquare}
          accent="blue"
          testId="metric-sms"
        />
        <MetricCard
          label="Talk Time"
          value={`${callMinutes} min`}
          sub={`${d?.calls?.total_seconds ? Math.floor(d.calls.total_seconds / 3600) : 0}h ${d?.calls?.total_seconds ? Math.floor((d.calls.total_seconds % 3600) / 60) : 0}m`}
          icon={Clock}
          testId="metric-talk-time"
        />
        <div className="col-span-2 lg:col-span-1">
          <MetricCard
            label="Active Lines"
            value={d?.numbers?.active ?? 0}
            sub={d?.numbers?.extra_lines > 0 ? `${d.numbers.extra_lines} extra ($${((d.numbers.extra_line_cost_cents || 0) / 100).toFixed(0)}/mo)` : `${d?.numbers?.included_lines ?? 0} included`}
            icon={Hash}
            accent={d?.numbers?.extra_lines > 0 ? 'amber' : 'green'}
            testId="metric-active-lines"
          />
        </div>
      </div>

      {/* Minutes gauge (billing-aligned) */}
      {d?.billing_cycle && (
        <MinutesGauge
          used={d.billing_cycle.used_minutes}
          included={d.billing_cycle.included_minutes}
          overage={d.billing_cycle.overage_minutes}
          tierName={d.billing_cycle.tier_name}
          overageChargeCents={d.billing_cycle.estimated_overage_charge_cents}
        />
      )}

      {/* Trend chart */}
      <TrendChart series={trends?.series} granularity={trends?.granularity} />

      {/* By-source rollup (only shown if sources exist) */}
      <BySourceRollup sources={bySource?.sources} />

      {/* Per-number table */}
      <PerNumberTable numbers={perNumber?.numbers} />
    </div>
  );
}
